import { useEffect, useRef, type ReactNode } from 'react';

import { applyShowLayersOnMap } from 'components/DisasterGPT/utils/addSelectedLayerForAI';
import { LayerSidebarPanelProvider } from 'components/MapPage/CorporateViewTabs/LayersTab/LayerSidebarPanel';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { SitStatSortableGrid } from 'components/MapPage/CorporateViewTabs/SitStatTab/SitStatSortableGrid';
import { SitStatWidgetCard } from 'components/MapPage/CorporateViewTabs/SitStatTab/SitStatWidgetCard';
import { renderSitStatWidget } from 'components/MapPage/CorporateViewTabs/SitStatTab/SitStatWorkspace';
import type { SitStatTemplate } from 'components/MapPage/CorporateViewTabs/SitStatTab/sitstat-template-types';
import {
  SITSTAT_WIDGET_TITLES,
  type SitStatWidgetId,
} from 'components/MapPage/CorporateViewTabs/SitStatTab/sitstat-widget-types';
import { useAorText } from 'utils/aorTerminology';
import { UscgMapContainer } from 'components/MapPage/UscgMapContainer';
import type { NestedSitrepSection } from 'components/Reports/document/nestSitrepSections';
import SitrepDocumentSection from 'components/Reports/document/SitrepDocumentSection';
import { ReportSlidePane } from 'components/Reports/presentation/ReportPresentation';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';

import type { PresentationSlide } from './presentationHooks';

const ACTIVE_HURRICANES_ITEM_ID = '248e7b5827a34b248647afb012c58787';
const ACTIVE_HURRICANES_NAME = 'active hurricanes / tropical cyclones (global)';

function useShowActiveHurricanes(enabled: boolean) {
  const mapCtx = useOptionalUscgMapContext();
  const groupGuid = useAppSelector(selectGroupGuid);
  const query = useListExternalLayersQuery(
    {
      filter: { groupGuid: groupGuid ?? '' },
      perPage: 200,
    },
    { enabled: enabled && Boolean(groupGuid) }
  );
  const shownRef = useRef(false);

  useEffect(() => {
    if (!enabled || shownRef.current || !mapCtx) return;
    const layer = (query.data?.data ?? []).find((entry) => {
      const name = entry.name.trim().toLowerCase();
      return (
        entry.arcgisItemId === ACTIVE_HURRICANES_ITEM_ID ||
        name === ACTIVE_HURRICANES_NAME
      );
    });
    if (!layer) return;
    applyShowLayersOnMap([{ kind: 'external', id: layer.id }], {
      isExternalLayerVisible: mapCtx.isExternalLayerVisible,
      toggleExternalLayerVisibility: mapCtx.toggleExternalLayerVisibility,
      isInternalLayerVisible: mapCtx.isInternalLayerVisible,
      toggleInternalLayerVisibility: mapCtx.toggleInternalLayerVisibility,
    });
    mapCtx.setIsMapVisible(true);
    shownRef.current = true;
  }, [enabled, mapCtx, query.data]);
}

function SlideFooter() {
  return (
    <div className="tw:flex tw:items-center tw:justify-end tw:border-t tw:border-slate-200 tw:px-6 tw:py-2 tw:text-xs tw:font-medium tw:text-[#0b3a82]">
      National Watch Center
    </div>
  );
}

export function CoverStage({ slide }: { slide: PresentationSlide }) {
  return (
    <div className="tw:flex tw:h-full tw:min-h-[28rem] tw:flex-col tw:bg-white tw:text-[#0b3a82]">
      <div className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:px-10 tw:text-center">
        {slide.eyebrow ? (
          <p className="tw:text-2xl tw:font-semibold">{slide.eyebrow}</p>
        ) : null}
        <h2 className="tw:mt-6 tw:text-5xl tw:font-bold">
          {slide.title || 'Cover'}
        </h2>
        {slide.dateLine ? (
          <p className="tw:mt-8 tw:text-2xl">{slide.dateLine}</p>
        ) : null}
        {slide.timeLine ? (
          <p className="tw:mt-2 tw:text-2xl">{slide.timeLine}</p>
        ) : null}
        {slide.mark === 'fema' ? (
          <p className="tw:mt-10 tw:text-sm tw:font-semibold tw:tracking-[0.3em]">
            FEMA
          </p>
        ) : null}
      </div>
      {slide.mark === 'fema' ? <SlideFooter /> : null}
    </div>
  );
}

function QuadrantGrid({
  cells,
}: {
  cells: { title: string; bullets: string[] }[];
}) {
  return (
    <div className="tw:grid tw:min-h-0 tw:flex-1 tw:grid-cols-2 tw:grid-rows-2">
      {[0, 1, 2, 3].map((index) => {
        const cell = cells[index];
        return (
          <div
            key={index}
            className="tw:min-h-0 tw:overflow-auto tw:border tw:border-slate-200 tw:p-4"
          >
            {cell ? (
              <>
                <h3 className="tw:text-center tw:text-lg tw:font-semibold">
                  {cell.title}
                </h3>
                {cell.bullets.length > 0 ? (
                  <ul className="tw:mt-3 tw:list-disc tw:space-y-1 tw:pl-5 tw:text-sm tw:text-slate-900">
                    {cell.bullets.map((bullet, bulletIndex) => (
                      <li key={`${index}-${bulletIndex}`}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function BriefingCopy({ slide }: { slide: PresentationSlide }) {
  return (
    <div className="tw:overflow-auto tw:p-6">
      <h2 className="tw:text-3xl tw:font-bold">{slide.title}</h2>
      {slide.subtitle ? (
        <p className="tw:mt-4 tw:text-lg tw:font-semibold">{slide.subtitle}</p>
      ) : null}
      <ul className="tw:mt-4 tw:list-disc tw:space-y-2 tw:pl-5 tw:text-base tw:text-slate-900">
        {(slide.bullets ?? []).map((bullet, bulletIndex) => (
          <li key={bulletIndex}>{bullet}</li>
        ))}
      </ul>
    </div>
  );
}

export function BriefingStage({
  slide,
  embedMap,
}: {
  slide: PresentationSlide;
  embedMap: boolean;
}) {
  useShowActiveHurricanes(slide.showMap === true);
  if (slide.showMap) {
    return (
      <div className="tw:flex tw:h-full tw:min-h-[28rem] tw:flex-col tw:bg-white tw:text-[#0b3a82]">
        <div className="tw:flex tw:min-h-0 tw:flex-1">
          <div
            className={
              embedMap ? 'tw:w-1/2 tw:overflow-auto' : 'tw:min-w-0 tw:flex-1'
            }
          >
            <BriefingCopy slide={slide} />
          </div>
          {embedMap ? (
            <div className="tw:min-h-[28rem] tw:w-1/2 tw:border-l tw:border-slate-200">
              <UscgMapContainer />
            </div>
          ) : null}
        </div>
        <SlideFooter />
      </div>
    );
  }
  return (
    <div className="tw:flex tw:h-full tw:min-h-[28rem] tw:flex-col tw:bg-white tw:text-[#0b3a82]">
      <h2 className="tw:border-b tw:border-slate-300 tw:px-6 tw:py-4 tw:text-center tw:text-3xl tw:font-bold">
        {slide.title}
      </h2>
      <QuadrantGrid cells={slide.quadrants ?? []} />
      <SlideFooter />
    </div>
  );
}

export function ReportWidgetsStage({
  sitrep,
  sections,
  offset,
  count,
}: {
  sitrep: GetSitrepsResponse;
  sections: NestedSitrepSection[];
  offset: number;
  count: 1 | 4;
}) {
  const page = sections.slice(offset, offset + count);
  if (page.length === 0) {
    return (
      <p className="tw:p-6 tw:text-sm tw:text-muted-foreground">
        This report has no sections.
      </p>
    );
  }
  const cells =
    count === 4 ? Array.from({ length: 4 }, (_, index) => page[index]) : page;
  return (
    <div
      className={
        count === 4
          ? 'tw:grid tw:h-full tw:min-h-[28rem] tw:grid-cols-2 tw:grid-rows-2 tw:gap-2 tw:overflow-hidden'
          : 'tw:h-full tw:min-h-[28rem] tw:overflow-auto'
      }
    >
      {cells.map((section, index) => (
        <div
          key={section?.id ?? `empty-${offset + index}`}
          className="tw:min-h-0 tw:overflow-auto"
        >
          {section ? (
            <SitrepDocumentSection
              section={section}
              sitrepId={sitrep.id}
              sitrep={sitrep}
              sitrepWorkspaceId={sitrep.incident_id}
              sitrepTemplateId={sitrep.sitrep_template_id}
              canEdit={false}
              showAuthoringActions={false}
              childSections={section.children}
              onUpdated={() => undefined}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function WidgetStrip({ ids }: { ids: SitStatWidgetId[] }) {
  const aorText = useAorText();
  if (ids.length === 0) return null;
  return (
    <div className="tw:flex tw:max-h-40 tw:shrink-0 tw:gap-2 tw:overflow-auto tw:px-2">
      {ids.map((id) => (
        <SitStatWidgetCard
          key={id}
          title={aorText(SITSTAT_WIDGET_TITLES[id])}
          className="tw:min-w-56 tw:flex-1"
          contentClassName="tw:max-h-28 tw:overflow-auto"
        >
          {renderSitStatWidget(id)}
        </SitStatWidgetCard>
      ))}
    </div>
  );
}

function CurrentView({ template }: { template: SitStatTemplate }) {
  return (
    <LayerSidebarPanelProvider>
      <SitStatSortableGrid
        layout={template.layout}
        onLayoutChange={() => undefined}
        leftCollapsed={template.uiState.leftCollapsed === true}
        rightCollapsed={template.uiState.rightCollapsed === true}
        onLeftCollapsedChange={() => undefined}
        onRightCollapsedChange={() => undefined}
        renderWidget={renderSitStatWidget}
        mapSlot={<UscgMapContainer />}
        className="tw:h-full tw:min-h-0"
      />
    </LayerSidebarPanelProvider>
  );
}

export function ViewStage({
  template,
  viewLayout,
}: {
  template: SitStatTemplate;
  viewLayout: 'current' | 'quad' | 'framed';
}) {
  const aorText = useAorText();
  if (viewLayout === 'quad') {
    const widgets = [...template.layout.left, ...template.layout.right].slice(
      0,
      4
    );
    return (
      <div className="tw:grid tw:h-full tw:min-h-[28rem] tw:grid-cols-2 tw:grid-rows-2 tw:gap-2">
        {[0, 1, 2, 3].map((index) => {
          const id = widgets[index];
          return (
            <div key={index} className="tw:min-h-0 tw:overflow-auto">
              {id ? (
                <SitStatWidgetCard
                  title={aorText(SITSTAT_WIDGET_TITLES[id])}
                  className="tw:h-full"
                  contentClassName="tw:h-full tw:overflow-auto"
                >
                  {renderSitStatWidget(id)}
                </SitStatWidgetCard>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }
  const current: ReactNode = (
    <div className="tw:h-full tw:min-h-0">
      <CurrentView template={template} />
    </div>
  );
  if (viewLayout === 'framed') {
    return (
      <div className="tw:flex tw:h-full tw:min-h-[28rem] tw:flex-col tw:gap-2">
        <WidgetStrip ids={template.layout.left} />
        <div className="tw:min-h-0 tw:flex-1">{current}</div>
        <WidgetStrip ids={template.layout.right} />
      </div>
    );
  }
  return <div className="tw:h-full tw:min-h-[28rem]">{current}</div>;
}

export function ReportDocumentStage({
  sitrep,
  section,
}: {
  sitrep: GetSitrepsResponse;
  section: NestedSitrepSection | undefined;
}) {
  return <ReportSlidePane sitrep={sitrep} section={section} />;
}
